import { MinusSquare, PlusSquare } from 'lucide-react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

const inlineStyles = {
  toggle: {
    width: '1em',
    height: '1em',
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
  contents: {
    willChange: 'transform, opacity, height',
    padding: '0px 0px 0px 15px',
  },
};

function Contents({ children, ...style }, open) {
  const newStyle = open
    ? { opacity: 1, height: 'auto', ...style }
    : { opacity: 0, height: 0, ...style };

  return open ? <div style={{ ...newStyle, ...inlineStyles.contents }}>{children}</div> : null;
}

const usePrevious = (values) => {
  const ref = useRef();

  useEffect(() => {
    if (values) {
      ref.current = values.map((child) => child.key);
    }
  });
  return ref.current;
};

function NewClassificationTree(props) {
  const [state, setState] = useState({
    open: props.open || false,
    visible: props.visible || false,
  });

  const {
    has_children,
    children,
    content,
    style,
    onClick,
    onExpand,
    reference,
    referenceDirect,
    disabledOptionsFlag,
    is_disabled = false,
  } = props;
  const prevChildren = usePrevious(children);

  const toggle = () => {
    if (!state.open && !props.children) {
      onExpand();
    }
    if (props.children) {
      setState({ ...state, open: !state.open, visible: false });
    }
  };

  useEffect(() => {
    if (
      props.children &&
      JSON.stringify(prevChildren) !== JSON.stringify(props.children.map((child) => child.key))
    ) {
      setState({ ...state, open: true, visible: false });
    } else {
      const newState = { ...state, ...props };
      setState(newState);
    }
  }, [props]);

  const renderReferenceBudge = () => {
    if (props.pickerMode)
      return (
        <div className="ml-auto flex items-center gap-1 pl-2 text-[10px]">
          <span
            title="Celkový počet včetně podkategorií"
            className="rounded-full bg-neutral-100 text-neutral-600 px-1.5"
          >
            {reference ?? 0}
          </span>
          {props.selectedCount > 0 && (
            <span
              title="Vybráno v této skupině"
              className="rounded-full bg-primary-100 text-primary-700 px-1.5"
            >
              {props.selectedCount}
            </span>
          )}
        </div>
      );
    const badges = [];
    if (reference > 0 && reference !== referenceDirect) {
      badges.push(
        <div
          key="total"
          className={classNames(
            'h-5 min-w-5 px-1 text-[10px] rounded-full flex items-center justify-center ml-1 font-mono',
            props.active ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'
          )}
          title="Celkový počet včetně podkategorií"
        >
          {reference}
        </div>
      );
    }
    if (referenceDirect > 0) {
      badges.push(
        <div
          key="direct"
          className={classNames(
            'h-5 min-w-5 px-1 text-[10px] border rounded-full flex items-center justify-center ml-1 font-mono',
            props.active ? 'border-white/30 text-white/80' : 'border-neutral-300 text-neutral-500'
          )}
          title="Přímé záznamy v této kategorii"
        >
          {referenceDirect}
        </div>
      );
    }

    return badges.length > 0 ? <div className="flex items-center">{badges}</div> : null;
  };

  const showDisabledOptions = disabledOptionsFlag && is_disabled;

  return (
    <div className="truncate text-neutral-700 text-sm" style={{ ...style }}>
      <div
        className={classNames(
          'h-[32px] items-center justify-between border-b border-neutral-100 py-[5px] px-2.5 transition-colors',
          {
            'text-white bg-primary-700 rounded': props.active,
            'hover:bg-neutral-50': !props.active,
            'opacity-50': showDisabledOptions,
          }
        )}
      >
        <span className="flex">
          {/* <Icon style={{ ...inlineStyles.toggle, opacity: 1 }} onClick={toggle} /> */}
          <span className="mr-[5px] flex self-center">
            {children && state.open && (
              <MinusSquare
                role="button"
                tabIndex={0}
                aria-label="Sbalit skupinu"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle();
                  }
                }}
                className="h-4 w-4"
                data-testid="classification-collapse-btn"
                onClick={toggle}
              />
            )}
            {has_children && !state.open && (
              <PlusSquare
                role="button"
                tabIndex={0}
                aria-label="Rozbalit skupinu"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggle();
                  }
                }}
                className="h-4 w-4"
                data-testid="classification-expand-btn"
                onClick={toggle}
              />
            )}
          </span>
          <span
            className="pl-[5px] cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis"
            data-testid="classification-item-select"
            role="button"
            tabIndex={0}
            aria-current={props.active ? 'true' : undefined}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }}
            onClick={onClick}
          >
            {content}
          </span>
          {renderReferenceBudge()}
        </span>
      </div>
      {Contents(props, state.open)}
    </div>
  );
}

export default NewClassificationTree;
